import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map, switchMap, of, firstValueFrom, catchError, from, Observable, shareReplay } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateService } from '@ngx-translate/core';
import { AiCapabilityFormComponent } from '../capabilities/ai-capability-form/ai-capability-form.component';
import { StickyFooterComponent } from '../sticky-footer/sticky-footer.component';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { AiToolFormComponent } from '../tools/ai-tool-form/ai-tool-form.component';
import { AiCapabilityForm, AiToolForm } from '../../core/forms';
import { AiGoalService } from '../../core/services/ai-goal.service';
import { AiToolFormRegistryService } from '../../core/services/ai-tool-form-registry.service';
import { AiAssistantService } from '../../core/services/ai-assistant.service';
import { AiFormSubmissionService } from '../../core/services/ai-form-submission.service';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../core/tokens';
import {
  PromptModuleApiService,
  ConfigurableGoalInterface,
  PromptModuleKey,
  FunctionApiService,
  GoalInterface,
  GoalType,
} from '@vendasta/ai-assistants';
import { CanComponentDeactivate } from '../../core/services/unsaved-changes-guard-v2';
import { getNamespace, globalNamespace } from '../../core/services/ai-assistant-utils';
import { Namespace } from '@vendasta/ai-assistants';

type ActiveSubForms =
  | {
      toolForm?: undefined;
    }
  | {
      toolForm: AiToolForm;
    };

@Component({
  selector: 'ai-capability-form-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    GalaxyPageModule,
    GalaxyLoadingSpinnerModule,
    GalaxyAlertModule,
    AiCapabilityFormComponent,
    StickyFooterComponent,
    AiAssistantI18nModule,
    AiToolFormComponent,
  ],
  providers: [AiToolFormRegistryService, AiFormSubmissionService],
  templateUrl: './ai-capability-form-page.component.html',
  styleUrl: './ai-capability-form-page.component.scss',
})
export class AiCapabilityFormPageComponent implements CanComponentDeactivate {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly goalService = inject(AiGoalService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly promptModuleApiService = inject(PromptModuleApiService);
  private readonly toolFormRegistry = inject(AiToolFormRegistryService);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly functionApiService = inject(FunctionApiService);
  private readonly formSubmissionService = inject(AiFormSubmissionService);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  private readonly capabilityId$ = this.route.paramMap.pipe(map((params) => params.get('capabilityId')));
  private readonly namespaceType$ = this.route.paramMap.pipe(map((params) => params.get('namespace') || 'local'));

  private readonly capabilityId = toSignal(this.capabilityId$);
  private readonly namespaceType = toSignal(this.namespaceType$);

  private readonly capability$ = combineLatest([
    this.capabilityId$,
    this.namespaceType$,
    this.accountGroupId$,
    this.partnerId$,
  ]).pipe(
    switchMap(([capabilityId, namespaceType, accountGroupId, partnerId]): Observable<GoalInterface | null> => {
      if (!capabilityId) {
        const namespace = this.getNamespaceFromType(namespaceType, accountGroupId, partnerId);
        if (!namespace) {
          return of(null);
        }
        return of({
          constraints: [],
          description: '',
          functions: [],
          id: '',
          name: '',
          namespace: namespace,
          promptModules: [],
          type: GoalType.GOAL_TYPE_CUSTOM,
        });
      }

      // Determine the correct namespace to use based on the URL parameter
      const targetNamespace = this.getNamespaceFromType(namespaceType, accountGroupId, partnerId);
      if (!targetNamespace) {
        console.error('Unable to determine namespace for type:', namespaceType);
        this.snackbarService.openErrorSnack('Invalid namespace');
        return of(null);
      }

      return this.goalService.getGoalFromNamespace(capabilityId, targetNamespace).pipe(
        catchError((error) => {
          console.error('Error loading capability from namespace:', { namespaceType, error });
          this.snackbarService.openErrorSnack('Error loading capability');
          return of(null);
        }),
      );
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private readonly promptModuleContents$ = this.capability$.pipe(
    switchMap((capability): Observable<Record<string, string | undefined>> => {
      if (!capability) {
        return of({});
      }

      if (!capability.promptModules?.length) {
        return of({});
      }

      // Create PromptModuleKey objects for all prompt modules in the capability
      const promptModuleKeys = capability.promptModules.map(
        (pm) => new PromptModuleKey({ id: pm.id, namespace: pm.namespace }),
      );

      if (promptModuleKeys.length === 0) {
        return of({});
      }

      // Use the same method as ai-assistant-form-v2 service
      return from(this.aiAssistantService.getMultiPromptModuleVersions(promptModuleKeys)).pipe(
        catchError((error) => {
          console.warn('Failed to load prompt module contents:', error);
          return of({});
        }),
      );
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly capability = toSignal(this.capability$);
  private readonly promptModuleContents = toSignal(this.promptModuleContents$, { initialValue: {} });

  readonly loading = signal(false);
  private readonly activeSubForms = signal<ActiveSubForms>({});
  protected readonly capabilityForm = signal<AiCapabilityForm | null>(null);
  readonly creatingForm = signal(false);

  constructor() {
    // TODO: Might need to handle capability does not exist

    // Effect to create capability form asynchronously when capability or prompt contents change
    effect(async () => {
      const capability = this.capability();
      const promptContents = this.promptModuleContents();

      if (!capability) {
        this.capabilityForm.set(null);
        this.creatingForm.set(false);
        return;
      }

      this.creatingForm.set(true);
      try {
        const configurableGoal: ConfigurableGoalInterface = {
          goal: capability,
          configuration: [],
        };

        const form = await AiCapabilityForm.createWithFunctionDetails(
          this.promptModuleApiService,
          configurableGoal,
          promptContents,
          this.toolFormRegistry,
          this.functionApiService,
        );

        // Disable the form if the capability is global or managed
        if (capability.namespace?.globalNamespace || capability.managed) {
          form.disable();
        }

        this.capabilityForm.set(form);
      } catch (error) {
        console.error('Error creating capability form:', error);
        this.snackbarService.openErrorSnack('AI_ASSISTANT.GOALS.ADD_GOAL.ERROR_LOADING_CAPABILITY');
        this.capabilityForm.set(null);
      } finally {
        this.creatingForm.set(false);
      }
    });
  }

  readonly activeToolForm = computed(() => this.activeSubForms().toolForm);

  readonly isGlobalOrManagedCapability = computed(() => {
    const capability = this.capability();
    if (!capability) return false;
    return !!(capability.namespace?.globalNamespace || capability.managed);
  });

  canDeactivate(): boolean {
    const form = this.capabilityForm();
    return !form || !form.dirty;
  }

  protected handleToolFormDisplay(toolForm: AiToolForm | undefined): void {
    if (!toolForm) {
      this.activeSubForms.set({});
      return;
    }
    this.activeSubForms.set({ toolForm });
  }

  protected closeToolForm(): void {
    this.activeSubForms.set({});
  }

  protected async handleToolFormSubmit(): Promise<void> {
    const toolForm = this.activeToolForm();
    const capabilityForm = this.capabilityForm();

    if (!toolForm || toolForm.disabled) {
      this.closeToolForm();
      return;
    }

    if (!toolForm.valid) {
      toolForm.markAllAsTouched();
      this.snackbarService.openErrorSnack(
        this.translateService.instant('AI_ASSISTANT.SETTINGS.ASSISTANT_VALIDATION_ERROR'),
      );
      return;
    }

    if (capabilityForm) {
      const submittedToolId = toolForm.controls.id.getRawValue();
      
      const existingTool = capabilityForm.controls.tools.controls.find((tool) => {
        if (!submittedToolId) {
          return tool.metadata.localId && tool.metadata.localId === toolForm.metadata.localId;
        }
        return tool.controls.id.getRawValue() === submittedToolId;
      });

      if (!existingTool) {
        capabilityForm.controls.tools.push(toolForm);
      }
    }
    // Close the tool form view after successful submission
    this.closeToolForm();
  }

  protected async handleCapabilityFormSubmit(): Promise<void> {
    const form = this.capabilityForm();
    const accountGroupId = await firstValueFrom(this.accountGroupId$);
    const partnerId = await firstValueFrom(this.partnerId$);
    const hasRequiredIds = accountGroupId || partnerId;

    if (!form || form.disabled || !hasRequiredIds) {
      return;
    }

    if (!form.valid) {
      form.markAllAsTouched();
      this.snackbarService.openErrorSnack(
        this.translateService.instant('AI_ASSISTANT.SETTINGS.ASSISTANT_VALIDATION_ERROR'),
      );
      return;
    }

    this.loading.set(true);

    try {
      await this.saveCapability(form, accountGroupId, partnerId);
      form.markAsPristine();
      this.snackbarService.openSuccessSnack('AI_ASSISTANT.SETTINGS.CAPABILITY_UPDATED');
      this.navigateBack();
    } catch (error) {
      console.error('Error saving capability:', error);
      this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.CAPABILITY_UPDATE_ERROR');
    } finally {
      this.loading.set(false);
    }
  }

  private async saveCapability(form: AiCapabilityForm, accountGroupId: string, partnerId: string): Promise<void> {
    if (!(accountGroupId || partnerId)) {
      throw new Error('Account group ID or partner ID is missing');
    }

    await this.formSubmissionService.submitCapability(form, { accountGroupId, partnerId });
  }

  protected handleCancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    const capabilityId = this.capabilityId();
    if (!capabilityId) {
      this.router.navigate(['..'], { relativeTo: this.route });
    } else {
      this.router.navigate(['../../..'], { relativeTo: this.route });
    }
  }

  private getNamespaceFromType(namespaceType: string, accountGroupId: string, partnerId: string): Namespace | null {
    switch (namespaceType) {
      case 'global':
        return globalNamespace;
      case 'local':
        return getNamespace(accountGroupId, partnerId) || null;
      case 'partner':
        return new Namespace({ partnerNamespace: { partnerId } });
      case 'system':
        return new Namespace({ systemNamespace: {} });
      default:
        return null;
    }
  }
}
